// src/v2/components/modules/ReportSTL/ReportSTLModule.js
import React, { useState, useCallback } from 'react';
import { useFilterManager } from '../../../../hooks/useFilterManager';
import { DynamicFilterBar } from '../../../filters/DynamicFilterBar';
import { LoadingSpinner } from '../../../../../common/Loading';
import { Button } from '../../../../../common/APICommon';

/**
 * Reusable Report Select Component
 */
const ReportSelect = ({ label, value, onChange, options, placeholder }) => {
  const hasValue = value !== "" && value !== null && value !== undefined;
  const safeOptions = options || [];

  return (
    <div className="flex flex-col w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <select
        className={`h-[34px] border rounded px-2 text-sm border-gray-300 transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none
          ${hasValue ? "text-gray-700 font-semibold" : "text-gray-400"}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {placeholder && <option value="" className="text-gray-400">{placeholder}</option>}
        {safeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
};

export const ReportSTLModule = ({ reportHook }) => {
  const currentYear = new Date().getFullYear();
  const [month, setMonth] = useState("");
  const [year, setYear] = useState(currentYear.toString());

  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    resetFilters,
    loadingEnums
  } = useFilterManager(['COMPANY', 'DEPARTMENT', 'CLASSIFICATION']);

  const handleClear = useCallback(() => {
    resetFilters();
    setMonth("");
    setYear(currentYear.toString());
  }, [resetFilters, currentYear]);

  const handleGenerate = () => {
    if (!activeFilterState.COMPANY || !month || !year) {
      alert("Please select Company, Month, and Year");
      return;
    }

    const queryParams = {
      companyId: activeFilterState.COMPANY.id,
      month: month,
      year: year,
      filters: JSON.stringify(apiFilterPayload)
    };

    const companyInfo = {
      name: activeFilterState.COMPANY.name || activeFilterState.COMPANY.fullName || "",
      tin: activeFilterState.COMPANY.tin || "",
      address: activeFilterState.COMPANY.address || ""
    };

    // Trigger direct Fetch + Download
    reportHook.generateAndDownload(queryParams, month, year, companyInfo);
  };

  const monthsOptions = [
    { label: "January", value: "January" }, { label: "February", value: "February" },
    { label: "March", value: "March" }, { label: "April", value: "April" },
    { label: "May", value: "May" }, { label: "June", value: "June" },
    { label: "July", value: "July" }, { label: "August", value: "August" },
    { label: "September", value: "September" }, { label: "October", value: "October" },
    { label: "November", value: "November" }, { label: "December", value: "December" }
  ];

  const yearsOptions = Array.from({ length: 10 }, (_, i) => ({
    label: (currentYear - i).toString(),
    value: (currentYear - i).toString()
  }));

  return (
    <div className="ReportsInfo shadow-xl bg-white rounded-lg p-6 border border-gray-100">

      {/* Breadcrumb Style - PRESERVED */}
      <div className='flex items-center text-sm mb-2'>
        <span className="text-gray-400">Reports</span>
        <span className="mx-2 text-gray-400">{">"}</span>
        <span className="border-b border-gray-800 text-gray-800 font-medium">Short-Term Loan Remittance (STL)</span>
      </div>

      {/* H3 Section - PRESERVED */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-customMainColor tracking-tight">Short-Term Loan Remittance (STL)</h3>
      </div>

      <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 mb-6">
        <div className="mb-4">
          <DynamicFilterBar
            requestedFilters={['COMPANY', 'DEPARTMENT', 'CLASSIFICATION']}
            activeFilterState={activeFilterState}
            onFilterChange={onFilterChange}
            showButtons={false}
            gridClass="grid grid-cols-1 md:grid-cols-3 gap-6 my-2"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="flex flex-col justify-center my-2">
            <ReportSelect label="Month" placeholder="Select Month" value={month} onChange={setMonth} options={monthsOptions} />
          </div>
          <div className="flex flex-col justify-center my-2">
            <ReportSelect label="Year" value={year} onChange={setYear} options={yearsOptions} />
          </div>
        </div>

        <div className="flex gap-3 mt-8 border-t border-gray-200 pt-5">
          <Button
            name="GENERATE & DOWNLOAD"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg font-bold shadow-sm transition-all"
            onClick={handleGenerate}
          />
          <Button
            name="CLEAR"
            className="bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 px-8 py-2.5 rounded-lg font-bold transition-all"
            onClick={handleClear}
          />
        </div>
      </div>

      <LoadingSpinner loading={reportHook.loading || loadingEnums} />
    </div>
  );
};